import { createAsyncThunk, createSlice } from "@reduxjs/toolkit"
import {
  getUserProfile,
  updateProfile,
  changePassword,
  getUserActivity,
} from "services/userService"

// ── Async thunks ──────────────────────────────────────────────────────────────

export const fetchProfile = createAsyncThunk(
  "user/fetchProfile",
  async (_, { rejectWithValue }) => {
    try {
      const data = await getUserProfile()
      if (!data?.status) return rejectWithValue(data?.msg || "Failed to fetch profile")
      return data.data
    } catch (err) {
      return rejectWithValue(err?.message || "Failed to fetch profile")
    }
  }
)

export const updateProfileThunk = createAsyncThunk(
  "user/updateProfile",
  async (userData, { rejectWithValue }) => {
    try {
      const data = await updateProfile(userData)
      if (!data?.status) return rejectWithValue(data?.msg || "Failed to update profile")
      return data.data
    } catch (err) {
      return rejectWithValue(err?.message || "Failed to update profile")
    }
  }
)

export const changePasswordThunk = createAsyncThunk(
  "user/changePassword",
  async ({ oldPassword, newPassword }, { rejectWithValue }) => {
    try {
      const data = await changePassword(oldPassword, newPassword)
      if (!data?.status) return rejectWithValue(data?.msg || "Failed to change password")
      return data
    } catch (err) {
      return rejectWithValue(err?.message || "Failed to change password")
    }
  }
)

export const fetchUserActivity = createAsyncThunk(
  "user/fetchActivity",
  async (_, { rejectWithValue }) => {
    try {
      const data = await getUserActivity()
      if (!data?.status) return rejectWithValue(data?.msg || "Failed to fetch activity")
      return data.data
    } catch (err) {
      return rejectWithValue(err?.message || "Failed to fetch activity")
    }
  }
)

// ── Slice ─────────────────────────────────────────────────────────────────────

const initialState = {
  profile: null,
  loading: false,
  error: null,
  preferences: {},
  activity: [],
}

const userSlice = createSlice({
  name: "user",
  initialState,
  reducers: {
    clearUserError(state) {
      state.error = null
    },
    setUserPreferences(state, action) {
      state.preferences = { ...state.preferences, ...action.payload }
    },
    clearUser(state) {
      state.profile = null
      state.error = null
      state.activity = []
    },
  },
  extraReducers: builder => {
    // fetchProfile
    builder
      .addCase(fetchProfile.pending, state => {
        state.loading = true
        state.error = null
      })
      .addCase(fetchProfile.fulfilled, (state, action) => {
        state.loading = false
        state.profile = action.payload
      })
      .addCase(fetchProfile.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload
      })

    // updateProfileThunk
    builder
      .addCase(updateProfileThunk.pending, state => {
        state.loading = true
        state.error = null
      })
      .addCase(updateProfileThunk.fulfilled, (state, action) => {
        state.loading = false
        state.profile = action.payload ?? state.profile
      })
      .addCase(updateProfileThunk.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload
      })

    // changePasswordThunk
    builder
      .addCase(changePasswordThunk.pending, state => {
        state.loading = true
        state.error = null
      })
      .addCase(changePasswordThunk.fulfilled, state => {
        state.loading = false
      })
      .addCase(changePasswordThunk.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload
      })

    // fetchUserActivity
    builder
      .addCase(fetchUserActivity.pending, state => {
        state.loading = true
        state.error = null
      })
      .addCase(fetchUserActivity.fulfilled, (state, action) => {
        state.loading = false
        state.activity = action.payload ?? []
      })
      .addCase(fetchUserActivity.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload
      })
  },
})

export const { clearUserError, setUserPreferences, clearUser } = userSlice.actions

// ── Selectors ─────────────────────────────────────────────────────────────────

export const selectProfile = state => state.UserSlice?.profile
export const selectUserLoading = state => state.UserSlice?.loading ?? false
export const selectUserError = state => state.UserSlice?.error
export const selectUserPreferences = state => state.UserSlice?.preferences ?? {}
export const selectUserActivity = state => state.UserSlice?.activity ?? []

export default userSlice.reducer
